import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert2";
import { LuEye, LuEyeOff } from "react-icons/lu";

import { Label } from "../../components/Label/styles";
import {
  BlueTitle,
  Column,
  ContainerWithBorder,
  LoadingGif,
  LoadingOverlay,
  StyledHR,
} from "../../shared/styles/global.styles";
import {
  Page,
  Logo,
  SantaCasaCunha,
  LoginContainer,
  LoginLabel,
  LoadingOverlayResponsive,
} from "./login.styles";
import { Colors } from "../../shared/colors/colors";
import LogoSantaCasaCunha from "../../assets/logo.png";
import loadSCGif from "../../assets/load_SC.gif";
import { useAuth } from "../../AuthContext/AuthContext";
import authService from "../../services/AuthService";
import Button from "../../components/Button/button";
import InputComponent from "../../components/Input/input";
import { GetControleAcessoDto } from "../../shared/dtos/ControleAcesso.dto";
import { ModulosEnum } from "../../shared/enum/modulos.enum";
import { PermissoesEnum } from "../../shared/enum/permicao.enum";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const inputRefs = useRef<{
    [key: string]: HTMLInputElement | HTMLSelectElement | null;
  }>({
    email: null,
    password: null,
  });

  const navigatePage = (controleAcesso: GetControleAcessoDto[]) => {
    if (
      controleAcesso?.some(
        (acesso) => acesso.modulos.nome === ModulosEnum.CONSULTA_RAPIDA
      )
    ) {
      return "/consulta-rapida";
    }

    if (
      controleAcesso?.some(
        (acesso) => acesso.modulos.nome === ModulosEnum.CONTRIBUINTE
      )
    ) {
      return "/list-contribuinte";
    }

    if (
      controleAcesso?.some(
        (acesso) =>
          acesso.modulos?.nome === ModulosEnum.LOTACOES ||
          acesso.modulos?.nome === ModulosEnum.LOCAL_PAGAMENTO ||
          acesso.modulos?.nome === ModulosEnum.INSTRUCOES_BANCARIAS ||
          acesso.modulos?.nome === ModulosEnum.FORMA_PAGAMENTO
      )
    ) {
      return "/cadastros-gerais";
    }

    if (
      controleAcesso?.some(
        (acesso) =>
          acesso.modulos.nome === ModulosEnum.REAJUSTE_VALORES_FINANCEIROS &&
          acesso.permissoes.role.includes(PermissoesEnum.ESCRITA)
      )
    ) {
      return "/reajuste-parcelas";
    }

    if (
      controleAcesso?.some(
        (acesso) => acesso.modulos.nome === ModulosEnum.CAIXA
      )
    ) {
      return "/caixa";
    }

    if (
      controleAcesso?.some(
        (acesso) =>
          acesso.modulos.nome === ModulosEnum.REMESSAS ||
          acesso.modulos.nome === ModulosEnum.RETORNOS
      )
    ) {
      return "/remessas";
    }

    if (
      controleAcesso?.some(
        (acesso) => acesso.modulos.nome === ModulosEnum.RELATORIOS_GERENCIAIS
      )
    ) {
      return "/relatorios-gerenciais";
    }

    if (
      controleAcesso?.some(
        (acesso) => acesso.modulos.nome === ModulosEnum.USUARIOS
      )
    ) {
      return "/list-users";
    }

    return undefined;
  };
  const handleLogin = async () => {
    // Validação dos campos antes de enviar
    const invalid = [];
    if (!email) invalid.push("email");
    if (!password) invalid.push("password");

    if (invalid.length > 0) {
      setInvalidFields(invalid);
      return;
    }

    setLoading(true);
    await authService
      .login({ email, password })
      .then((response) => {
        const { accessToken, user } = response;

        // Garante que todos os campos necessários estejam presentes
        if (!user.permission) {
          throw new Error("Usuário sem permissão definida");
        }

        // Garante que o ID seja um número válido
        const userId =
          typeof user.id === "string" ? parseInt(user.id, 10) : Number(user.id);

        if (isNaN(userId) || userId <= 0) {
          throw new Error("ID de usuário inválido");
        }

        const userToStore = {
          id: userId,
          name: user.name,
          email: user.email,
          permission: user.permission,
          isActive: user.isActive ?? true,
        };

        const userToAuth = {
          id: userId,
          name: user.name,
          email: user.email,
          permission: user.permission,
          isActive: user.isActive ?? true,
          controleAcesso: user.controleAcesso,
        };


        // Salva os dados no sessionStorage
        sessionStorage.setItem("authToken", accessToken);
        sessionStorage.setItem("user", JSON.stringify(userToStore));

        // Atualiza contexto de autenticação
        setToken(accessToken);
        setUser(userToAuth);
        const linkNavigate = navigatePage(user.controleAcesso);
        if (linkNavigate) {
          navigate(`${linkNavigate}`, { replace: true });
        } else {
          swal.fire({
            title: "Usuário sem acesso a nenhuma tela",
            text: "Socilite ao administrador o acesso a alguma tela!",
            icon: "warning",
            confirmButtonText: "Ok",
            confirmButtonColor: "#4c53af",
          });
        }
      })
      .catch((error) => {
        console.error("Erro ao fazer login:", error);
        swal.fire({
          title: "Erro ao fazer login",
          text: `${error.message}`,
          icon: "error",
          confirmButtonText: "Ok",
          confirmButtonColor: "#4c53af",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  // Função para limpar o erro do campo ao digitar
  const handleInputChange = (field: string, value: string) => {
    if (value.trim() !== "") {
      setInvalidFields((prev) => prev.filter((item) => item !== field)); // Remove o erro do campo
    }
    if (field === "email") setEmail(value);
    if (field === "password") setPassword(value);
  };

  return (
    <Page onKeyDown={handleKeyDown} tabIndex={0}>
      <div>
        <Logo>
          <SantaCasaCunha
            src={LogoSantaCasaCunha}
            alt="Logo da Santa Casa de Cunha - São Paulo"
          />
        </Logo>

        {loading && (
          <LoadingOverlay>
            <LoadingGif src={loadSCGif} alt="Carregando..." />
          </LoadingOverlay>
        )}

        <ContainerWithBorder>
          <BlueTitle>Login</BlueTitle>
          <StyledHR />
          <Column>
            <Label htmlFor="email">E-mail</Label>
            <InputComponent
              color="none"
              placeholder="exemplo@email.com"
              id="login-email"
              border="1px solid #afb1b6"
              width={336}
              value={email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              style={{
                outline: "none", // Remove o destaque no foco
                borderColor: "#afb1b6", // Estilo consistente na borda
              }}
              errorMessage={
                invalidFields.includes("email") ? "Preencha o campo Email" : ""
              }
              ref={(el) => (inputRefs.current["nome"] = el)}
            />
          </Column>
          <Column>
            <Label htmlFor="password">Senha</Label>
            <InputComponent
              color="none"
              placeholder="insira sua senha"
              id="login-password"
              border="1px solid #afb1b6"
              width={336}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              iconRight={
                showPassword ? (
                  <LuEyeOff size={24} color={Colors.inputIcon} />
                ) : (
                  <LuEye size={24} color={Colors.inputIcon} />
                )
              }
              onIconRightClick={() => setShowPassword((prev) => !prev)}
              style={{
                outline: "none", // Remove o destaque no foco
                borderColor: "#afb1b6", // Estilo consistente na borda
              }}
              errorMessage={
                invalidFields.includes("password")
                  ? "Preencha o campo Senha"
                  : ""
              }
              ref={(el) => (inputRefs.current["password"] = el)}
            />
          </Column>
          <Button
            type="button"
            schemaButton="full-wd-primary"
            action={() => handleLogin()}
          >
            Entrar
          </Button>
        </ContainerWithBorder>
      </div>
    </Page>
  );
};

export default LoginPage;
